import { useEffect, useRef } from 'react';

/**
 * Persists form state to localStorage so users don't lose data when they
 * navigate away (e.g. to check Xaman wallet) and return to the dialog.
 *
 * Behavior:
 *  - Loads saved state from localStorage[storageKey] once on mount.
 *  - Saves state on every change.
 *  - Provides clear() to wipe storage on successful submission.
 *
 * Note: state and setState come from the caller (useState in parent),
 * so the hook is purely a side-effect manager.
 */
export function useFormPersistence(storageKey, state, setState, options = {}) {
  const { skipKeys = [], enabled = true } = options;
  const loadedRef = useRef(false);

  // Restore on first mount
  useEffect(() => {
    if (!enabled || loadedRef.current) return;
    loadedRef.current = true;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && typeof saved === 'object') {
        // Merge — keep current defaults for any newly-added keys
        setState((prev) => ({ ...prev, ...saved }));
      }
    } catch (e) {
      console.warn(`[useFormPersistence] Corrupt entry for "${storageKey}" — clearing.`, e);
      try { localStorage.removeItem(storageKey); } catch (err) {
        console.warn('[useFormPersistence] Failed to clear corrupt entry:', err);
      }
    }
  }, [storageKey, enabled, setState]);

  // Save on every change
  useEffect(() => {
    if (!enabled || !loadedRef.current) return;
    try {
      const toSave = { ...state };
      skipKeys.forEach((k) => delete toSave[k]);
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch (e) {
      // localStorage unavailable (private mode / quota exceeded) — degrade silently but log
      console.warn(`[useFormPersistence] Could not save "${storageKey}":`, e?.message || e);
    }
  }, [storageKey, state, enabled, skipKeys.join(',')]);

  const clear = () => {
    try { localStorage.removeItem(storageKey); } catch (e) {
      console.warn(`[useFormPersistence] Failed to clear "${storageKey}":`, e);
    }
  };

  return { clear };
}
